import React from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import Flatpickr from 'react-flatpickr';
import { Vietnamese } from 'flatpickr/dist/l10n/vn';
import { Modal, ModalBody, ModalHeader, Label, Col } from 'reactstrap';
import { useLocation } from 'react-router-dom';

import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';
import { ReactComponent as ChevronDown } from 'assets/images/Chervon.svg';
import CalendarPlusIcon from 'assets/images/calendar-plus.svg';

import Button from 'components/App/Button';
import FormField from 'components/AddLocation/FormField';
import SelectValidation from 'components/App/SelectValidation';

import { OPTION_CANCEL_PENALTY, OPTION_ABSENT_PENALTY } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';

import styles from './ModalEditPolicyCancelRoomFlex.module.scss';

const OPTION_FREE_BEFORE_DAYS = {
  0: { value: 0, label: 'Không hoàn hủy' },
  1: { value: 1, label: 'Đến 1 ngày trước khi tới nhận phòng' },
  2: { value: 2, label: 'Đến 2 ngày trước khi tới' },
  3: { value: 3, label: 'Đến 3 ngày trước khi tới' },
  4: { value: 4, label: 'Đến 4 ngày trước khi tới' },
  5: { value: 5, label: 'Đến 5 ngày trước khi tới' },
  7: { value: 7, label: 'Đến 7 ngày trước khi tới' },
  10: { value: 10, label: 'Đến 10 ngày trước khi tới' },
  14: { value: 14, label: 'Đến 14 ngày trước khi tới' },
};

const optionFreeBeforeDays = [
  OPTION_FREE_BEFORE_DAYS[0],
  OPTION_FREE_BEFORE_DAYS[1],
  OPTION_FREE_BEFORE_DAYS[2],
  OPTION_FREE_BEFORE_DAYS[3],
  OPTION_FREE_BEFORE_DAYS[4],
  OPTION_FREE_BEFORE_DAYS[5],
  OPTION_FREE_BEFORE_DAYS[7],
  OPTION_FREE_BEFORE_DAYS[10],
  OPTION_FREE_BEFORE_DAYS[14],
];

const optionCancelPenalty = [
  OPTION_CANCEL_PENALTY.FIRST_NIGHT,
  OPTION_CANCEL_PENALTY['50_PERCENT'],
  OPTION_CANCEL_PENALTY['100_PERCENT'],
];

const optionAbsentPenalty = [
  OPTION_ABSENT_PENALTY.EQUAL_CANCEL_PENALTY,
  OPTION_ABSENT_PENALTY['100_PERCENT'],
];

const DEFAULT_FREE_BEFORE_DAYS = 0;
const DEFAULT_CANCEL_PENALTY = OPTION_CANCEL_PENALTY['100_PERCENT'].value;
const DEFAULT_ABSENT_PENALTY = OPTION_ABSENT_PENALTY['100_PERCENT'].value;

function ModalEditPolicyCancelRoomFlex(props) {
  const {
    cancelBookingPolicy,
    cancelBookingPolicyList,
    isCreateMode,
    isOpen,

    onToggleModal,
    actionGetPlaceDetail,
    actionEditCancelPolicy,
  } = props;
  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');

  const CloseButton = React.useMemo(
    () => (
      <button
        className={styles.ButtonClose}
        onClick={onToggleModal}
        type="button"
      >
        <CloseButtonSvg />
      </button>
    ),
    [onToggleModal],
  );

  const onCallBackSave = React.useCallback(() => {
    onToggleModal();
    actionGetPlaceDetail({ placeId });
  }, [actionGetPlaceDetail, onToggleModal, placeId]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      freeBeforeDays:
        cancelBookingPolicy?.freeBeforeDays ?? DEFAULT_FREE_BEFORE_DAYS,
      cancelPenalty:
        cancelBookingPolicy?.cancelPenalty ?? DEFAULT_CANCEL_PENALTY,
      absentPenalty:
        cancelBookingPolicy?.absentPenalty ?? DEFAULT_ABSENT_PENALTY,
      startAt: cancelBookingPolicy?.startAt ?? '',
      endAt: cancelBookingPolicy?.endAt ?? '',
      placeId,
    },

    onSubmit: () => {
      const isEmptyCancelBookingPolicyList = !cancelBookingPolicyList?.length;
      const policyPayload = {
        freeBeforeDays: validation.values.freeBeforeDays,
        cancelPenalty: validation.values.cancelPenalty,
        absentPenalty: validation.values.absentPenalty,
        startAt: validation.values.startAt,
        endAt: validation.values.endAt,
        isApplied: isCreateMode
          ? isEmptyCancelBookingPolicyList
          : cancelBookingPolicy?.isApplied || false,
      };

      const nextCancelBookingPolicy = isCreateMode
        ? [...cancelBookingPolicyList, policyPayload]
        : cancelBookingPolicyList.map((item) => (
          item._id === cancelBookingPolicy._id
            ? { ...policyPayload, _id: cancelBookingPolicy._id }
            : item
        ));

      actionEditCancelPolicy({
        placeId: validation.values.placeId,
        cancelBookingPolicy: nextCancelBookingPolicy.map((item) => ({
          ...(item._id ? { _id: item._id } : {}),
          freeBeforeDays: item.freeBeforeDays,
          cancelPenalty: item.cancelPenalty,
          absentPenalty: item.absentPenalty,
          startAt: item.startAt,
          endAt: item.endAt,
          isApplied: item.isApplied,
        })),
        onCallBackSave,
      });
    },
  });

  const onChangeFreeBeforeDays = React.useCallback(
    (e) => {
      validation.setFieldValue('freeBeforeDays', e.value, true);
      validation.setFieldValue(
        'cancelPenalty',
        optionCancelPenalty[2].value,
        true,
      );
      validation.setFieldValue('absentPenalty', DEFAULT_ABSENT_PENALTY, true);
    },
    [validation],
  );

  const onChangeCancelPenalty = React.useCallback(
    (e) => {
      validation.setFieldValue('cancelPenalty', e.value, false);
    },
    [validation],
  );

  const onChangeAbsentPenalty = React.useCallback(
    (e) => {
      validation.setFieldValue('absentPenalty', e.value, false);
    },
    [validation],
  );

  const onChangeStartAt = React.useCallback(
    (dates) => {
      const [selectedDate] = dates;
      validation.setFieldValue(
        'startAt',
        selectedDate ? selectedDate.toISOString() : '',
        false,
      );
    },
    [validation],
  );

  const onChangeEndAt = React.useCallback(
    (dates) => {
      const [selectedDate] = dates;
      validation.setFieldValue(
        'endAt',
        selectedDate ? selectedDate.toISOString() : '',
        false,
      );
    },
    [validation],
  );

  const RenderDescriptionCancelPolicy = React.useMemo(() => {
    const { freeBeforeDays, cancelPenalty } = validation.values;

    switch (true) {
      case freeBeforeDays === 0 && cancelPenalty === 'FIRST_NIGHT':
        return 'Khách không thể hủy đặt phòng miễn phí trước khi tới nhận phòng. Khách sẽ phải trả giá của đêm đầu tiên nếu hủy phòng trước khi tới nhận phòng.';
      case freeBeforeDays === 0 && cancelPenalty === '50_PERCENT':
        return `Khách không thể hủy đặt phòng miễn phí trước khi tới nhận phòng. Khách sẽ phải trả ${OPTION_CANCEL_PENALTY[cancelPenalty].label} nếu hủy phòng trước khi tới nhận phòng.`;
      case freeBeforeDays === 0 && cancelPenalty === '100_PERCENT':
        return 'Khách không thể hủy đặt phòng miễn phí trước khi tới nhận phòng. Khách sẽ phải trả toàn bộ tiền phòng nếu hủy phòng trước khi tới nhận phòng.';
      case freeBeforeDays !== 0 && cancelPenalty === 'FIRST_NIGHT':
        return `Khách có thể hủy miễn phí đến ${freeBeforeDays} ngày trước khi tới nhận phòng. Khách sẽ phải trả giá của đêm đầu tiên nếu hủy phòng trong vòng ${freeBeforeDays} ngày trước khi tới nhận phòng.`;
      case freeBeforeDays !== 0 && cancelPenalty === '50_PERCENT':
        return `Khách có thể hủy miễn phí đến ${freeBeforeDays} ngày trước khi tới nhận phòng. Khách sẽ phải trả ${OPTION_CANCEL_PENALTY[cancelPenalty].label} nếu hủy phòng trong vòng ${freeBeforeDays} ngày trước khi tới nhận phòng.`;
      case freeBeforeDays !== 0 && cancelPenalty === '100_PERCENT':
        return `Khách có thể hủy miễn phí đến ${freeBeforeDays} ngày trước khi tới nhận phòng. Khách sẽ phải trả toàn bộ tiền phòng nếu hủy phòng trong vòng ${freeBeforeDays} ngày trước khi tới nhận phòng.`;
      default:
        return 'Khách không thể hủy đặt phòng miễn phí trước khi tới nhận phòng. Khách sẽ phải trả toàn bộ tiền phòng nếu hủy phòng trước khi tới nhận phòng.';
    }
  }, [validation.values]);

  const RenderDescriptionAbsentPenalty = React.useMemo(() => {
    const { absentPenalty } = validation.values;

    if (absentPenalty === OPTION_ABSENT_PENALTY.EQUAL_CANCEL_PENALTY.value) {
      return 'Nếu khách không đến, họ sẽ phải trả phí bằng với phí huỷ đặt phòng.';
    }

    return 'Nếu khách không đến, họ sẽ phải trả toàn bộ tiền phòng.';
  }, [validation.values]);

  return (
    <div className={styles.Component}>
      <Modal
        fade
        size="lg"
        isOpen={isOpen}
        toggle={onToggleModal}
        className={styles.Modal}
      >
        <ModalHeader
          toggle={onToggleModal}
          className={styles.ModalHeader}
          close={CloseButton}
        />

        <ModalBody className={styles.ModalBody}>
          <Label className="Label">
            {validation.values.freeBeforeDays !== 0
              ? `Linh động - ${validation.values.freeBeforeDays} ngày`
              : 'Không hoàn hủy'}
          </Label>

          <hr className="Divider" />

          <FormField label="Khách có thể hủy đặt phòng miễn phí bao lâu trước khi tới nơi?">
            <SelectValidation
              placeholder="Đến 3 ngày trước khi tới nơi"
              options={optionFreeBeforeDays}
              value={validation.values.freeBeforeDays}
              validation={validation}
              onChange={onChangeFreeBeforeDays}
              className={styles.Select}
            />
          </FormField>

          <FormField
            label={
              validation.values.freeBeforeDays !== 0
                ? `Khách phải trả bao nhiêu khi hủy đặt phòng trong vòng ${validation.values.freeBeforeDays} ngày trước khi tới nhận phòng?`
                : 'Khách phải trả bao nhiêu khi hủy đặt phòng trước khi tới nhận phòng?'
            }
          >
            <SelectValidation
              placeholder="100% tiền phòng"
              options={optionCancelPenalty}
              value={validation.values.cancelPenalty}
              validation={validation}
              isDisabled={validation.values.freeBeforeDays === 0}
              onChange={onChangeCancelPenalty}
              defaultValue={optionCancelPenalty[2].value}
            />
          </FormField>

          <FormField label="Bạn sẽ thu phí bao nhiêu nếu khách vắng mặt?">
            <SelectValidation
              placeholder="100% tiền phòng"
              options={optionAbsentPenalty}
              value={validation.values.absentPenalty}
              validation={validation}
              isDisabled={validation.values.freeBeforeDays === 0}
              onChange={onChangeAbsentPenalty}
              defaultValue={DEFAULT_ABSENT_PENALTY}
            />
          </FormField>

          <FormField label="Giai đoạn áp dụng">
            <div className={styles.DatePickerWrapper}>
              <div className={styles.DatePickerField}>
                <img
                  src={CalendarPlusIcon}
                  alt="calendar-plus"
                  className={styles.DatePickerField__IconLeft}
                />
                <Flatpickr
                  value={validation.values.startAt}
                  onChange={onChangeStartAt}
                  options={{ dateFormat: 'd/m/Y', locale: Vietnamese }}
                  placeholder="Ngày bắt đầu"
                />
                <ChevronDown className={styles.DatePickerField__IconRight} />
              </div>
              <div className={styles.DatePickerField}>
                <img
                  src={CalendarPlusIcon}
                  alt="calendar-plus"
                  className={styles.DatePickerField__IconLeft}
                />
                <Flatpickr
                  value={validation.values.endAt}
                  onChange={onChangeEndAt}
                  options={{ dateFormat: 'd/m/Y', locale: Vietnamese }}
                  placeholder="Ngày kết thúc"
                />
                <ChevronDown className={styles.DatePickerField__IconRight} />
              </div>
            </div>
          </FormField>

          <hr className="Divider" />

          <div className="Block__Policy__Wrapper">
            <Col md="5" className="Block__Title mb-8">
              Tóm tắt chính sách
            </Col>

            <div className="Block__Policy__Information__Wrapper">
              <div className="Block__Policy__Information__Content">
                {RenderDescriptionCancelPolicy}
                {''}
                {RenderDescriptionAbsentPenalty}
              </div>
            </div>
          </div>

          <hr className="Divider" />

          <div className="Button__Wrapper">
            <Button classes="Button Button__Cancel" onClick={onToggleModal}>
              Hủy
            </Button>

            <Button
              classes="Button Button__Save"
              onClick={validation.handleSubmit}
            >
              {isCreateMode ? 'Tạo' : 'Lưu'}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

ModalEditPolicyCancelRoomFlex.propTypes = {
  cancelBookingPolicy: PropTypes.instanceOf(Object),
  cancelBookingPolicyList: PropTypes.instanceOf(Array),
  isCreateMode: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,

  onToggleModal: PropTypes.func.isRequired,
  actionGetPlaceDetail: PropTypes.func.isRequired,
  actionEditCancelPolicy: PropTypes.func.isRequired,
};

ModalEditPolicyCancelRoomFlex.defaultProps = {
  cancelBookingPolicy: INITIAL_VALUE.OBJECT,
  cancelBookingPolicyList: [],
  isCreateMode: false,
};

export default React.memo(ModalEditPolicyCancelRoomFlex);
